import type {
  IngressConfigurationWire,
  ManagedAwsTrustBootstrapWire,
  ProvisioningConfigurationWire,
} from "@quovy/control-plane-contract";

/**
 * Every call to the customer portal goes through here.
 *
 * The portal authenticates with an HTTP-only cookie (`cp_portal_session`), not
 * the operator console's Google ALB OIDC session. A 401 means "sign in", not
 * "reload the page to re-enter OIDC". Reloading here would loop a signed-out
 * customer through the SPA forever.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export class SessionExpired extends ApiError {
  constructor(message = "Sign in to the customer portal, then retry.") {
    super(message, 401);
  }
}

const REQUEST_TIMEOUT_MS = 15_000;

function readErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const error = (body as { error?: unknown }).error;
  if (typeof error === "string") return error;
  if (error !== undefined) return JSON.stringify(error);
  return fallback;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      const seconds = REQUEST_TIMEOUT_MS / 1000;
      throw new ApiError(
        `The portal did not answer ${method} ${path} within ${seconds} seconds. Retry the request.`,
        408,
      );
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
  }

  if (response.status === 401) {
    let message = "Sign in to the customer portal, then retry.";
    try {
      message = readErrorMessage(await response.json(), message);
    } catch {
      /* Keep the default. */
    }
    throw new SessionExpired(message);
  }

  if (!response.ok) {
    let message = response.statusText;
    try {
      message = readErrorMessage(await response.json(), message);
    } catch {
      /* statusText is the most useful thing left. */
    }
    throw new ApiError(message, response.status);
  }

  const text = await response.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(
      `Expected JSON from ${path} but got ${response.headers.get("content-type") ?? "no content type"}.`,
      response.status,
    );
  }
}

export type PortalMe = {
  user: { id: string; email: string };
  organization: { id: string; name: string };
};

export type PortalEnvironment = {
  public_id: string;
  slug: string;
  display_name: string;
  cloud: string;
  region: string;
  blocked: string | null;
  status: string;
};

export type CreatePortalEnvironmentInput = {
  slug: string;
  display_name: string;
  cloud: "aws" | "azure";
  region: string;
  provisioning:
    | { kind: "customer_provided" }
    | { kind: "quovy_managed_aws"; aws_account_id: string };
  endpoints?: { web_origin: string; api_origin: string };
  ingress?: IngressConfigurationWire;
};

export type CreatePortalEnvironmentResult = {
  environment: string;
  public_id: string;
  blocked: string;
  approval_policy: "manual";
  provisioning:
    | null
    | Extract<ProvisioningConfigurationWire, { kind: "customer_provided" }>
    | (Extract<ProvisioningConfigurationWire, { kind: "quovy_managed_aws" }> & {
        trust: ManagedAwsTrustBootstrapWire;
      });
  next: string[];
};

export type AgentTokenResult = {
  environment: string;
  token: string;
  rotated_by: string;
  note: string;
};

export const portal = {
  me: () => request<PortalMe>("GET", "/v1/portal/me"),
  environments: () =>
    request<{ environments: PortalEnvironment[] }>("GET", "/v1/portal/environments"),
  createEnvironment: (input: CreatePortalEnvironmentInput) =>
    request<CreatePortalEnvironmentResult>("POST", "/v1/portal/environments", input),
  mintAgentToken: (publicId: string) =>
    request<AgentTokenResult>("POST", `/v1/portal/environments/${publicId}/agent-token`),
  logout: () => request<{ ok: true }>("POST", "/v1/portal/auth/logout"),
  devLogin: (input: { email: string; organization_name?: string }) =>
    request<PortalMe>("POST", "/v1/portal/auth/dev-login", input),
};

export function startPortalSignIn() {
  window.location.assign("/v1/portal/auth/start");
}
